import React, { useEffect, useState, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Container, Dimmer, Dropdown, Form, Icon, Loader, Message, Pagination, Radio, Table } from 'semantic-ui-react';
import './warehouse.admin.scss';
import { ADMIN_LIST_PAGINATION_ROW_CLASS } from '../list/list';
import { formatDateTime } from '../../../utils/shared.utils';


type RowState = { minusCount: string; note: string };
type SortKey = 'createdAt' | 'remaining';

const PAGE_SIZE_OPTIONS = [
    { key: 10, value: 10, text: '10' },
    { key: 15, value: 15, text: '15' },
    { key: 25, value: 25, text: '25' },
    { key: 50, value: 50, text: '50' },
    { key: 100, value: 100, text: '100' },
];

export default function AdminWarehouseMinusPageComponent(props: any) {
    const history = useHistory();
    const id = props.match.params.id;

    const [warehouse, setWarehouse] = useState<{ id: number; name: string; count: number; minCount: number; slug: string } | null>(null);
    const [importsWithRemaining, setImportsWithRemaining] = useState<any[]>([]);
    const [decreaseNote, setDecreaseNote] = useState('');
    const [noImportDecreaseCount, setNoImportDecreaseCount] = useState('');
    const [rowInputs, setRowInputs] = useState<Record<number, RowState>>({});
    const [activeImportId, setActiveImportId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [page, setPage] = useState({ page: 1, size: 15 });
    const [sortKey, setSortKey] = useState<SortKey>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            history.push('/');
            return;
        }
        fetch(process.env.REACT_APP_API_URL + 'mongol/warehouse/' + id, {
            method: 'GET',
            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
        })
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((data) => {
                if (data?.warehouse) setWarehouse(data.warehouse);
                setImportsWithRemaining(Array.isArray(data?.importsWithRemaining) ? data.importsWithRemaining : []);
            })
            .catch(() => setWarehouse(null))
            .finally(() => setFetching(false));
    }, [id, history]);

    const sortedImports = useMemo(() => {
        const list = [...importsWithRemaining];
        list.sort((a, b) => {
            let va: number | string = 0;
            let vb: number | string = 0;
            if (sortKey === 'createdAt') {
                va = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                vb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            } else {
                va = a.remaining ?? 0;
                vb = b.remaining ?? 0;
            }
            if (va < vb) return sortOrder === 'asc' ? -1 : 1;
            if (va > vb) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return list;
    }, [importsWithRemaining, sortKey, sortOrder]);

    const paginatedImports = useMemo(() => {
        const total = sortedImports.length;
        const totalPages = Math.max(1, Math.ceil(total / page.size));
        const start = (page.page - 1) * page.size;
        return {
            list: sortedImports.slice(start, start + page.size),
            totalPages,
            totalRows: total,
        };
    }, [sortedImports, page.page, page.size]);

    useEffect(() => {
        if (page.page > paginatedImports.totalPages && paginatedImports.totalPages >= 1) {
            setPage((p) => ({ ...p, page: 1 }));
        }
    }, [paginatedImports.totalPages, page.page]);

    const currentCount = warehouse?.count ?? 0;
    const hasImports = importsWithRemaining.length > 0;

    const getDecreaseItemsArray = () => {
        const items: { warehouseImportId: number; minusCount: number; note: string }[] = [];
        if (activeImportId != null) {
            const imp = importsWithRemaining.find((i) => i.id === activeImportId);
            if (imp) {
                const r = rowInputs[activeImportId] || { minusCount: '', note: '' };
                const minus = parseInt(r.minusCount, 10) || 0;
                const note = (r.note || '').trim();
                if (minus > 0 && note) items.push({ warehouseImportId: activeImportId, minusCount: minus, note });
            }
        }
        return items;
    };

    const decreaseAmountFromImports = hasImports ? getDecreaseItemsArray().reduce((s, i) => s + i.minusCount, 0) : 0;
    const noImportDecreaseNum = parseInt(noImportDecreaseCount, 10) || 0;
    const decreaseAmount = hasImports ? decreaseAmountFromImports : Math.max(0, noImportDecreaseNum);
    const newCountNum = currentCount - decreaseAmount;
    const overWarehouseCount = decreaseAmount > currentCount;
    const noImportOverCount = !hasImports && noImportDecreaseNum > currentCount;
    const activeRow = activeImportId != null ? importsWithRemaining.find((i) => i.id === activeImportId) : null;
    const anyImportOverCount =
        hasImports &&
        activeRow &&
        (parseInt(rowInputs[activeImportId!]?.minusCount || '0', 10) || 0) > (activeRow.remaining ?? 0);

    const canSubmit =
        !loading &&
        (hasImports
            ? decreaseAmount > 0 &&
              getDecreaseItemsArray().length > 0 &&
              !overWarehouseCount &&
              !anyImportOverCount
            : noImportDecreaseNum > 0 &&
              (decreaseNote || '').trim() !== '' &&
              !noImportOverCount);

    const setRow = (importId: number, field: 'minusCount' | 'note', value: string) => {
        setRowInputs((prev) => ({
            ...prev,
            [importId]: { ...(prev[importId] || { minusCount: '', note: '' }), [field]: value },
        }));
    };

    const handleSort = (key: SortKey) => {
        setSortKey(key);
        setSortOrder((o) => (sortKey === key ? (o === 'asc' ? 'desc' : 'asc') : 'desc'));
    };

    const handlePageChange = (_: any, data: any) => {
        setPage((p) => ({ ...p, page: data.activePage }));
        window.scrollTo(0, 100);
    };

    const handlePageSizeChange = (_: any, data: any) => {
        setPage((p) => ({ ...p, size: data.value, page: 1 }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('adminToken');
        if (!token || !warehouse) return;
        if (decreaseAmount <= 0) {
            setErrorMsg(hasImports ? 'Нэг мөр сонгоод хасагдах тоо болон тайлбар оруулна уу.' : 'Хасах тоо оруулна уу.');
            return;
        }
        const decreaseItems = getDecreaseItemsArray();
        if (hasImports && decreaseItems.length === 0) {
            setErrorMsg('Сонгосон мөрт хасагдах тоо болон тайлбар заавал оруулна уу.');
            return;
        }
        if (hasImports && decreaseAmount > 0 && activeImportId != null) {
            const r = rowInputs[activeImportId] || { minusCount: '', note: '' };
            const minus = parseInt(r.minusCount, 10) || 0;
            if (minus > 0 && !(r.note || '').trim()) {
                setErrorMsg('Тайлбар заавал оруулна уу.');
                return;
            }
        }
        if (hasImports && decreaseAmount > currentCount) {
            setErrorMsg('Нийт хасагдах тоо одоогийн үлдэгдлээс хэтрэхгүй байх ёстой.');
            return;
        }
        if (!hasImports) {
            const noteTrim = (decreaseNote || '').trim();
            if (!noteTrim) {
                setErrorMsg('Тайлбар заавал оруулна уу.');
                return;
            }
            if (noImportDecreaseNum > currentCount) {
                setErrorMsg('Хасах тоо одоогийн үлдэгдлээс хэтрэхгүй байх ёстой.');
                return;
            }
        }
        setErrorMsg('');
        setLoading(true);
        const payload: any = {
            count: newCountNum,
            minCount: warehouse.minCount,
            decreaseItems: hasImports ? decreaseItems : [{ warehouseImportId: null, minusCount: noImportDecreaseNum, note: (decreaseNote || '').trim() }],
        };
        fetch(process.env.REACT_APP_API_URL + 'mongol/warehouse/' + id + '/minus', {
            method: 'POST',
            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then(async (r) => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setErrorMsg(data.error || 'Алдаа гарлаа');
                    return;
                }
                setSuccessMsg('Амжилттай хадгаллаа.');
                setRowInputs({});
                setActiveImportId(null);
                setDecreaseNote('');
                setNoImportDecreaseCount('');
                setTimeout(() => setSuccessMsg(''), 3000);
                return fetch(process.env.REACT_APP_API_URL + 'mongol/warehouse/' + id, {
                    method: 'GET',
                    headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
                });
            })
            .then((refetchRes) => {
                if (refetchRes && refetchRes.ok) return refetchRes.json();
            })
            .then((refetchData) => {
                if (refetchData?.warehouse) setWarehouse(refetchData.warehouse);
                if (Array.isArray(refetchData?.importsWithRemaining)) setImportsWithRemaining(refetchData.importsWithRemaining);
            })
            .finally(() => setLoading(false));
    };

    if (fetching) {
        return (
            <div className="page-admin">
                <Container>
                    <p>Ачааллаж байна...</p>
                </Container>
            </div>
        );
    }
    if (!warehouse) {
        return (
            <div className="page-admin">
                <Container>
                    <p>Агуулах олдсонгүй.</p>
                </Container>
            </div>
        );
    }

    return (
        <section className="page-admin full-width">
            <Dimmer active={loading} inverted>
                <Loader size="medium" />
            </Dimmer>
            <div className="form-group">
                <Link to={'/mongol/warehouse/' + id}>
                    <Icon name="arrow left" />
                    <span className="underline">Буцах</span>
                </Link>
            </div>
            <h2>{warehouse.name}</h2>
            <div className="admin-warehouse-form">
                <div className="warehouse-count-only">
                <div className="label">Одоогийн нийт үлдэгдэл</div>
                <div className="count-value">{currentCount}</div>
            </div>
            </div>
            
        
            <Form onSubmit={handleSubmit}>
                {errorMsg && (
                    <div className="error-msg">
                        <Icon name="warning circle" />
                        {errorMsg}
                    </div>
                )}
                {successMsg && (
                    <div className="success-msg">
                        <Icon name="checkmark" />
                        {successMsg}
                    </div>
                )}

                {!hasImports && (
                    <>
                        <Form.Input
                            label="Хасах тоо *"
                            type="number"
                            min={1}
                            max={currentCount}
                            value={noImportDecreaseCount}
                            onChange={(_, d) => setNoImportDecreaseCount(String(d.value))}
                            placeholder="0"
                            error={noImportOverCount}
                        />
                        {noImportDecreaseNum > 0 && <p className="decrease-info">Нийт хасах: {noImportDecreaseNum}</p>}
                        {noImportOverCount && (
                            <Message negative size="small">
                                <Icon name="exclamation triangle" /> Хасах тоо одоогийн үлдэгдлээс ({currentCount}) хэтрэхгүй байх ёстой.
                            </Message>
                        )}
                        <Form.Input
                            label="Тайлбар *"
                            placeholder="Тайлбар оруулна уу"
                            value={decreaseNote}
                            onChange={(_, d) => setDecreaseNote(String(d.value))}
                        />
                    </>
                )}

                {hasImports && (
                    <>
                        <p className="margin-top-20">Нийт {paginatedImports.totalRows} Өртөг бүртгэл (үлдэгдэлтэй)</p>
                        <Table striped className="admin-list-table" width={16}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell width="1">Сонгох</Table.HeaderCell>
                                    <Table.HeaderCell>№</Table.HeaderCell>
                                    <Table.HeaderCell className="sortable" onClick={() => handleSort('createdAt')}>
                                        Огноо {sortKey === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </Table.HeaderCell>
                                    <Table.HeaderCell className="sortable" onClick={() => handleSort('remaining')}>
                                        Үлдэгдэл {sortKey === 'remaining' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Өртөг</Table.HeaderCell>
                                    <Table.HeaderCell>Нэмсэн тайлбар</Table.HeaderCell>
                                    <Table.HeaderCell>Нэмсэн хэрэглэгч</Table.HeaderCell>
                                    <Table.HeaderCell>Хасагдах тоо</Table.HeaderCell>
                                    <Table.HeaderCell>Хасагдах тайлбар</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {paginatedImports.list.map((imp: any, idx: number) => {
                                    const r = rowInputs[imp.id] || { minusCount: '', note: '' };
                                    const minusNum = parseInt(r.minusCount, 10) || 0;
                                    const remaining = imp.remaining ?? 0;
                                    const isActive = activeImportId === imp.id;
                                    const hasMinusCount = minusNum > 0;
                                    const noteEmpty = !(r.note || '').trim();
                                    const noteError = hasMinusCount && noteEmpty;
                                    const overImportCount = minusNum > remaining;
                                    const rowDanger = noteError || overImportCount;
                                    const rowNum = (page.page - 1) * page.size + idx + 1;
                                    return (
                                        <Table.Row key={imp.id} className={rowDanger ? 'error-row' : ''}>
                                            <Table.Cell data-label="Сонгох">
                                                <Radio
                                                    name="activeImport"
                                                    checked={isActive}
                                                    onChange={() => setActiveImportId(imp.id)}
                                                />
                                            </Table.Cell>
                                            <Table.Cell data-label="№">{rowNum}</Table.Cell>
                                            <Table.Cell data-label="Огноо">{formatDateTime(new Date(imp.createdAt))}</Table.Cell>
                                            <Table.Cell data-label="Үлдэгдэл">{imp.remaining != null ? imp.remaining : '–'}</Table.Cell>
                                            <Table.Cell data-label="Өртөг">{imp.cost != null ? imp.cost : '–'}</Table.Cell>
                                            <Table.Cell data-label="Нэмсэн тайлбар">{imp.note != null ? imp.note : '–'}</Table.Cell>
                                            <Table.Cell data-label="Нэмсэн хэрэглэгч">{imp.createdBy != null ? imp.createdBy : '–'}</Table.Cell>
                                            <Table.Cell data-label="Хасагдах тоо">
                                                <Form.Input
                                                    size="small"
                                                    type="number"
                                                    min={0}
                                                    max={remaining}
                                                    value={r.minusCount}
                                                    onChange={(_, d) => setRow(imp.id, 'minusCount', String(d.value))}
                                                    placeholder="0"
                                                    error={overImportCount}
                                                    disabled={!isActive}
                                                />
                                                {overImportCount && <span className="danger-hint">Үлдэгдэл: {remaining}</span>}
                                            </Table.Cell>
                                            <Table.Cell data-label="Хасагдах тайлбар">
                                                <Form.Input
                                                    size="small"
                                                    value={r.note}
                                                    onChange={(_, d) => setRow(imp.id, 'note', String(d.value))}
                                                    placeholder={isActive ? 'Тайлбар * (заавал)' : 'Тайлбар'}
                                                    required={hasMinusCount}
                                                    error={noteError}
                                                    disabled={!isActive}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>

                        <div className={ADMIN_LIST_PAGINATION_ROW_CLASS}>
                            <Pagination
                                activePage={page.page}
                                totalPages={Math.max(1, paginatedImports.totalPages)}
                                onPageChange={handlePageChange}
                            />
                            <div className="page-size-selector flex-div justify-content-center align-items-center">
                                <div className="flex-div align-items-center" style={{ marginRight: '10px' }}>ХУУДАСНЫ ХЭМЖЭЭ:</div>
                                <Dropdown
                                    selection
                                    upward
                                    options={PAGE_SIZE_OPTIONS}
                                    value={page.size}
                                    onChange={handlePageSizeChange}
                                    placeholder="Хуудасны хэмжээ"
                                    compact
                                />
                            </div>
                        </div>

                        {decreaseAmount > 0 && <p className="decrease-info">Нийт хасах: {decreaseAmount}</p>}
                        {overWarehouseCount && (
                            <Message negative size="small">
                                <Icon name="exclamation triangle" /> Нийт хасагдах тоо ({decreaseAmount}) одоогийн агуулах үлдэгдлээс ({currentCount}) хэтрэхгүй байх ёстой.
                            </Message>
                        )}
                    </>
                )}

                <Button
                    type="submit"
                    color="red"
                    disabled={!canSubmit}
                >
                    Хадгалах
                </Button>
            </Form>
        </section>
    );
}
